import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import { ChartHeader, ResolutionToggle, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin } from "./chartTheme";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";

ChartJS.register(zoomPlugin, annotationPlugin);

// Define colors array outside component to maintain consistency
const colors = [
    '#3e58c0', // Indigo
    '#e88559', // Tangerine
    '#9792ec', // Lilac
    '#aa4851', // Scarlet
    '#866263', // Royal Purple
    '#76ba92', // Key Lime
    '#0bcaca', // Light Turquoise
    '#f1dc78', // Lemon Yellow
    '#3c6e5f', // Army Green
];

// Function to get color based on index
const getColorForIndex = (index) => {
  if (index < 0 || index >= colors.length) {
    return '#000000'; // Return black for out-of-bounds indices
  }
  return colors[index];
};

// Function to convert game start time to annotation timestamp
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime) => {
  if (!gameDate || !gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  const annotationTime = new Date(`${gameDate}T${gameStartTime}:00`);
  
  return annotationTime.toISOString();
};

const ConcessionTransactionChart = ({ historicalTransactionData, sections, formatTransactionTime, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);
  const chartRef = useRef(null);
  const [chartResolution, setChartResolution] = useState(15);
  const [dataTimeRange, setDataTimeRange] = useState({ min: null, max: null });
  const resolutions = [1, 5, 10, 15, 30, 60];

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  const getIntervalKey = (timePoint, resolution) => {
    const roundedTime = new Date(timePoint);
    roundedTime.setMinutes(Math.floor(roundedTime.getMinutes() / resolution) * resolution, 0, 0);
    return roundedTime.toISOString();
  };

  // Function to calculate the time range from all data points
  const calculateDataTimeRange = useCallback((data) => {
    if (!data || data.length === 0) return { min: null, max: null };
    
    let minTime = null;
    let maxTime = null;
    
    data.forEach(sectionData => {
      sectionData.data.forEach(point => {
        const time = new Date(point.x);
        if (!minTime || time < minTime) minTime = time;
        if (!maxTime || time > maxTime) maxTime = time;
      });
    });
    
    return { min: minTime, max: maxTime };
  }, []);

  // Resolution control functions

  const aggregateDataByResolution = useCallback((data, resolution) => {
    return data.map(sectionData => {
      const aggregatedData = new Map();

      sectionData.data.forEach(point => {
        const key = getIntervalKey(new Date(point.x), resolution);
        if (!aggregatedData.has(key)) {
          aggregatedData.set(key, {
            sum: 0,
            count: 0,
            max: 0,
            min: Infinity
          });
        }
        const entry = aggregatedData.get(key);
        entry.sum += point.y;
        entry.count += 1;
        entry.max = Math.max(entry.max, point.y);
        entry.min = Math.min(entry.min, point.y);
      });

      const aggregatedPoints = Array.from(aggregatedData.entries()).map(([key, value]) => ({
        x: key,
        y: value.max // Changed from average to maximum
      }));

      return {
        section: sectionData.section,
        data: aggregatedPoints.sort((a, b) => new Date(a.x) - new Date(b.x))
      };
    });
  }, []);

  useEffect(() => {
    if (historicalTransactionData && historicalTransactionData.length > 0) {
      const aggregatedData = aggregateDataByResolution(historicalTransactionData, chartResolution);
      setProcessedData(aggregatedData);
      
      // Calculate time range from aggregated data
      const timeRange = calculateDataTimeRange(aggregatedData);
      setDataTimeRange(timeRange);
      
      setLoading(false);
    }
  }, [historicalTransactionData, chartResolution, aggregateDataByResolution, calculateDataTimeRange]);

  const chartData = {
    datasets: processedData.map((sectionData, index) => ({
      label: `${sectionData.section}`,
      data: sectionData.data,
      ...sectionLineDataset(getColorForIndex(index)),
    }))
  };

  const maxValue = Math.max(
    ...processedData.flatMap(dataset => 
      dataset.data.map(point => point.y)
    ),
    60 // Minimum of 60 seconds for transaction times
  );

  // Create game start annotation
  const gameStartAnnotation = currentGame?.date && currentGame?.gameStartTime ? 
    convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime) : null;

  const chartOptions = {
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          displayFormats: {
            hour: 'h:mm A'
          },
          stepSize: chartResolution >= 30 ? 1 : 0.5
        },
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
        // Dynamic min/max based on actual data range
        min: dataTimeRange.min,
        max: dataTimeRange.max
      },
      y: {
        min: 0,
        max: Math.ceil(maxValue / 10) * 10,
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "Transaction Time (seconds)",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          stepSize: 10,
          callback: function(value) {
            return formatTransactionTime ? formatTransactionTime(value) : `${value}s`;
          }
        }
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleTimeString('en-US', { 
              hour: 'numeric',
              minute: '2-digit'
            });
          },
          label: (context) => {
            const formattedTime = formatTransactionTime ? 
              formatTransactionTime(context.parsed.y) : 
              `${context.parsed.y}s`;
            return `${context.dataset.label}: ${formattedTime}`;
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: function(chart) {
            // Get default labels for the datasets
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            // Add game start indicator if we have one - put it FIRST
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: 'Game Start',//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1 // Special index to identify this as non-clickable
              };
              
              // Put game start at the beginning
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          // Only handle clicks for dataset items (not game start)
          if (legendItem.index !== -1) {
            // Use the default legend click behavior for dataset items
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
          // Do nothing for game start (index === -1)
        }
      },
      zoom: {
        limits: {
          // Lock zoom limits to data range
          x: { 
            min: dataTimeRange.min, 
            max: dataTimeRange.max, 
            minRange: 60 * 60 * 1000 // Minimum 1 hour range
          },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
  };

  if (!loading && processedData.length > 0) {
    return (
      <div className="graph panel-2 cx-chart-card">
        <ChartHeader
          title={"Section Transaction Times"}
          subtitle={"Average register transaction time by section. Each point is the highest reading in that interval."}
          controls={<ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={setChartResolution} />}
        />
        <div style={{ position: 'relative', height: '300px', marginTop: '8px' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'auto',
          }}>
            <div style={{ width: '100%', height: '100%' }}>
              <Line ref={chartRef} data={chartData} options={applyChartTheme(chartOptions)} plugins={[hoverGuidePlugin]} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <p>Loading...</p>;
  }

  return null; // Don't render anything if no transaction data
};

export default ConcessionTransactionChart;